import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { INPUT_CLASSES } from '@/lib/theme'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import athleteApi from '@/lib/api'
import { Eye, EyeOff, Check } from 'lucide-react'

const MIN_LENGTH = 8

/**
 * First screen after an athlete clicks the link in their welcome email. We already know who they
 * are, so the only thing to collect is a password; then straight into profile setup.
 */
const CreatePassword: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser, logout } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)

  const firstName = athlete?.name?.split(' ')[0] ?? 'there'
  const longEnough = password.length >= MIN_LENGTH
  const matches = confirm.length > 0 && confirm === password
  const canSubmit = longEnough && matches && !saving

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    try {
      await athleteApi.patch('/users/me/password', { newPassword: password })
      await refreshUser()
      navigate('/welcome', { replace: true })
    } catch (err: any) {
      toast.error('Could not save your password', {
        description: err?.response?.data?.error ?? 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-10">
      <div className="fixed inset-0 -z-10 bg-background" />
      <div className="w-full max-w-md">
        <img
          src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png"
          alt="Athios"
          className="mx-auto mb-8 h-7"
        />
        <div className="rounded-[28px] bg-[#FFFFFF] p-6 shadow-[0_20px_60px_rgba(16,24,40,0.08)]">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4d7c0f]">
            Welcome to Athios
          </p>
          <h1 className="text-2xl font-bold mt-1 tracking-tight text-foreground">
            Hey {firstName}.
          </h1>
          <p className="mt-2 text-gray-600">
            Your account is ready. Create a password and we'll start building your profile.
          </p>

          <dl className="mt-5 space-y-2 rounded-xl border border-foreground/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Name</dt>
              <dd className="font-medium text-foreground">{athlete?.name}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Email</dt>
              <dd className="truncate font-medium text-foreground">{athlete?.email}</dd>
            </div>
          </dl>

          <form onSubmit={submit} data-track-form="Create password" className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-gray-700">
                Create a password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${INPUT_CLASSES.dark} pr-10`}
                  placeholder={`At least ${MIN_LENGTH} characters`}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600"
                  aria-label={show ? 'Hide password' : 'Show password'}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-gray-700">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={INPUT_CLASSES.dark}
                placeholder="Type it again"
              />
            </div>

            <ul className="space-y-1 text-xs text-gray-500">
              <li className={`flex items-center gap-2 ${longEnough ? 'text-[#4d7c0f]' : ''}`}>
                <Check className="h-3.5 w-3.5" /> {MIN_LENGTH} characters or more
              </li>
              <li className={`flex items-center gap-2 ${matches ? 'text-[#4d7c0f]' : ''}`}>
                <Check className="h-3.5 w-3.5" /> Both passwords match
              </li>
            </ul>

            <Button
              type="submit"
              variant="lime"
              className="h-12 w-full text-base"
              disabled={!canSubmit}
            >
              {saving ? 'Saving…' : 'Start my profile'}
            </Button>
          </form>
        </div>
        <p className="mt-4 text-center text-xs text-gray-500">
          Not {firstName}?{' '}
          <button
            type="button"
            onClick={() => {
              logout()
              navigate('/signin')
            }}
            className="text-gray-700 underline"
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  )
}

export default CreatePassword
